import React, { useState } from "react";
import { getWorkPackageActivitiesAPI, getWorkPackageInfoAPI, getPlmUserInfoAPI, deletePlmWorkpackageAPI, patchWorkPackageAPI } from "@/services/ppm"
import { getPlmProjectAllTeamUserApi } from '@/services'
import { useRequest } from "ahooks"
import { parseTime, toHsl, getPlmUserName, documentUtil } from "@/utils"
import { Modal, Select, Input, Button, Timeline, App, DatePicker } from "antd"
import type { DatePickerProps, FormProps } from 'antd';
import dayjs, { Dayjs } from "dayjs";
import clsx from "clsx";
import Markdown from "@/components/plm/marked";
import PlmEdit from "./plmEdit"
import { ZoomInSvg, ZoomOutSvg } from "@/components/svgs"

interface IProps{
    projectID: number
    packageID: string | number
    onClose: () => void
    onUpdate: () => void
}
type ActivityRender = Op.WorkPackageActivity & {
    user?: Op.User
}
let allPlmUserMap = new Map<number, Op.User>()

const getActivities = (taskId: number | string): Promise<ActivityRender[]> => {
    return new Promise(async (resolve, reject) => {
        if (!taskId) {
            resolve([])
            return
        }
        try {
            const actRes = await getWorkPackageActivitiesAPI(taskId)
            let userIDs: number[] = []
            const elements = actRes._embedded.elements.filter(v => v._type === "Activity::Comment").map(v => {
                let href = v._links.user.href
                const userID = Number(href.split("/").pop() || "")
                v.userID = userID
                userID && userIDs.push(userID)
                v.time = parseTime(v.updatedAt, "{y}-{m}-{d} {h}:{i}")
                return v
            })
            
            userIDs = Array.from(new Set(userIDs))
            let reqUserIDs = userIDs.filter(v => !allPlmUserMap.has(v))
            let reqPromises: Promise<Op.User>[] = []
            reqUserIDs.forEach(v => {
                reqPromises.push(getPlmUserInfoAPI(v))
            })
            let users = await Promise.all(reqPromises)
            users.forEach(v => {
                if (v) {
                    allPlmUserMap.set(v.id, v)
                }
            })
            let renderActs: ActivityRender[] = []
            elements.forEach(v => {
                if (!v.comment.raw && v.details.length > 0) {
                    let mds = v.details.map(v => `<p>${v.html}</p>`)
                    v.markdown = mds.join("\n")
                } else {
                    v.markdown = v.comment.raw || ""
                }
                v.createdAt = parseTime(v.createdAt, "{y}-{m}-{d} {h}:{i}")
                const user = allPlmUserMap.get(v.userID)
                if (user) {
                    renderActs.push({ ...v, user })
                    return
                }
                renderActs.push({ ...v})
            })
            resolve(renderActs)
        } catch (err) {
            reject(err)
        }
    })
}
const ActivityRow: React.FC<{ value: ActivityRender }> = (props) => {
    const { value } = props
    const useName = getPlmUserName(value.user)
    return (
        <div>
            <div className="flex items-center">
                <div className="w-[35px] h-[35px] leading-[35px] text-[12px] rounded-full text-white whitespace-nowrap scale-75 text-center" style={{ background: toHsl(useName) }}>{useName}</div>
                <span className="text-xs">{value.createdAt}</span>
            </div>
            <Markdown md={value.markdown}></Markdown>
        </div>
    )
}
const Index: React.FC<IProps> = (props) => {
    const [modal, contextHolder] = Modal.useModal();
    const [isFullScreen, setIsFullScreen] = useState(false)
    const { message } = App.useApp();

    const { data: packInfo, loading, run: fetchWorkPackageInfo } = useRequest(getWorkPackageInfoAPI, {
        defaultParams: [props.packageID]
    })
    const { data: activities, loading: activityLoading, run: fetchActivities, refresh:refreshActivities } = useRequest(getActivities, {
        defaultParams: [props.packageID]
    })
    const { data: allUser = [], loading: userLoading, run: fetchAllUsers } = useRequest(getPlmProjectAllTeamUserApi, {
        defaultParams: [props.projectID],
    })
    
    const { run: deleteWorkpackage } = useRequest(deletePlmWorkpackageAPI, {
        manual: true,
        onSuccess: () => {
            message.success("删除工作包成功")
            props.onClose()
            props.onUpdate()
        }
    })

    const onDeleteWorkpackage = () => {
        modal.confirm({
            title: <span>是否确认删除工作包 <span className="text-red-800">{props.packageID}</span> ?</span>,
            icon: null,
            onOk:()=> {
                deleteWorkpackage(props.packageID)
            },
            onCancel() {

            },
        });
    }
    const { run: updateWorkpackage } = useRequest(patchWorkPackageAPI, {
        manual: true,
        onSuccess(res) {
            message.success("更新工作包日期成功")
            fetchWorkPackageInfo(props.packageID)
            props.onUpdate()
        }
    })
    const [datePanelVisible, setDatePanelVisible] = useState(false);
    const onDateChange: DatePickerProps['onChange'] = (date, dateString) => {
        setDatePanelVisible(false)
        updateWorkpackage(props.packageID, { date: dateString as string, lockVersion: packInfo?.lockVersion || 0 })
    }
    return (
        <Modal title="" width="fit-content" style={{ top: isFullScreen ? 20 : 100 }} destroyOnHidden maskClosable={false} open={true} onCancel={props.onClose} footer={null}>
            {contextHolder}
            <div className={clsx(" transition-all duration-100 min-w-[500px]", isFullScreen ? "w-[calc(100vw-80px)] h-[calc(100vh-80px)] flex flex-col overflow-y-auto" : "w-[600px]")}>
                <div className="flex items-center justify-center cursor-pointer rounded-md absolute z-10 right-[50px] top-[12px] h-[32px] w-[32px] text-gray-500 transition duration-200 hover:bg-gray-200 hover:text-gray-700" onClick={() => setIsFullScreen(prev => !prev)}>
                    {isFullScreen ? (
                        <ZoomInSvg />
                    ) : (
                        <ZoomOutSvg />
                    )}
                </div>
                <div className="shrink-0 relative">
                    <a className="font-bold hover:underline cursor-pointer" href={`${documentUtil.getPpmProjectWorkPackagePath(props.packageID, props.projectID)}`} target="_blank" rel="noreferrer"><span className=" text-red-900">#{props.packageID}</span> {packInfo?.subject}</a>
                    
                    <div className="flex items-center gap-2">
                        <div className="flex items-center text-xs">
                            <span className="text-xs mr-1">状态</span>
                            <span style={{ backgroundColor: packInfo?._embedded.status.color }} className="rounded-full px-2 text-gray-600">{packInfo?._embedded.status.name}</span>
                        </div>
                        <div className="flex items-center">
                            <span className="text-xs mr-1">日期</span>
                            <div className="relative">
                                <span className="text-xs font-bold cursor-pointer px-2 py-1 rounded-full bg-gray-200 hover:bg-gray-300" onClick={()=>setDatePanelVisible(true)}>{packInfo?.date}</span>
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <DatePicker
                                        value={packInfo?.date ? dayjs(packInfo.date, "YYYY-MM-DD") : null}
                                        open={datePanelVisible}
                                        styles={{
                                            root: {
                                                pointerEvents: 'none',
                                                opacity: 0,
                                                position: 'absolute',
                                                top: -10,
                                                insetInlineStart: 0,
                                            },
                                        }}
                                        onOpenChange={setDatePanelVisible}
                                        onChange={onDateChange}
                                    />
                                </div>
                            </div>
                            
                        </div>
                        {packInfo?._embedded.assignee && (
                            <div className="flex items-center">
                                <span className="text-xs mr-1">指定人</span>
                                <div className="w-[35px] h-[35px] leading-[35px] text-[12px] rounded-full text-white whitespace-nowrap scale-75 text-center overflow-hidden"
                                    style={{ background: toHsl(getPlmUserName(packInfo?._embedded.assignee)) }}>
                                    {getPlmUserName(packInfo?._embedded.assignee)}
                                </div>
                            </div>
                        )}
                        {packInfo?._links?.parent.title && (
                            <div className="flex items-center">
                                <span className="text-xs mr-1 shrink-0">所属任务</span>
                                <div className="max-w-[160px] text-xs font-bold flex-1 whitespace-nowrap text-ellipsis overflow-hidden">{packInfo?._links?.parent.title}</div>
                            </div>
                        )}
                    </div>
                    <div className="text-xs text-gray-800">
                        所属项目：
                        <a className="text-xs hover:underline cursor-pointer text-gray-800" href={`${documentUtil.getPpmProjectPath(props.projectID)}`} target="_blank" rel="noreferrer">{packInfo?._embedded.project.name}</a>
                    </div>
                    {packInfo?.description?.html && (
                        <div className={clsx("text-xs text-gray-800 mt-2 flex")}>
                            <div className="shrink-0">描述：</div>
                            <div className="flex-1">
                                <Markdown md={packInfo?.description?.html}></Markdown>
                            </div>
                        </div>
                            
                    )}
                    
                    <div className="mt-2 font-bold text-xs mb-1">最新活动</div>
                </div>
                <div className={clsx("border border-l-0 border-r-0 border-gray-100 border-solid p-4")}>
                    <Timeline items={activities?.map((v, i) => ({
                        children: <ActivityRow value={v} />
                    }))} />
                    {activities && activities.length === 0 && !activityLoading && (
                        <div className="py-24 text-xs text-gray-400 text-center">暂无活动数据</div>
                    )}
                </div>
                <div className={clsx("mt-4 relative", isFullScreen && "shrink-0")}>
                    
                    {/* <Input.TextArea placeholder="请输入工作详情描述" /> */}
                    <PlmEdit packageID={props.packageID} users={allUser} onClose={props.onClose} onUpdate={refreshActivities}></PlmEdit>
                    <div className="absolute left-0 bottom-0 z-10">
                        <Button danger onClick={onDeleteWorkpackage}>删除</Button>
                    </div>
                </div>
            </div>
        </Modal>
        
    )
}
export default Index